import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from 'axios';
import { refreshToken } from '@/shared/lib/refreshToken';
import { tokenStorage } from '@/shared/lib/tokenStorage';
import { emitAuthFailure } from '@/shared/lib/authEvents';
import { AUTH_ERROR_CODES, mapAxiosError } from './errors';

// axios 인스턴스 생성
const baseURLEnv =
  typeof import.meta.env.VITE_API_URL === 'string' ? import.meta.env.VITE_API_URL : '';
const axiosClient: AxiosInstance = axios.create({
  baseURL: baseURLEnv,
  timeout: 30000, // 30초로 증가 (백엔드 응답 대기)
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터
axiosClient.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getToken();
    if (token) {
      if (!config.headers) config.headers = new AxiosHeaders();
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: unknown) =>
    Promise.reject(error instanceof Error ? error : new Error('Request configuration failed')),
);

// 응답 인터셉터
interface RetriableRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

axiosClient.interceptors.response.use(
  (response) => response,
  async (rawError: unknown) => {
    const error = rawError as AxiosError;
    const originalRequest = error.config as RetriableRequestConfig | undefined;

    // 401이 아닌 에러는 기존대로 도메인 에러 변환
    if (!error.response || error.response.status !== 401 || !originalRequest) {
      return Promise.reject(mapAxiosError(error));
    }

    const responseData = error.response.data as
      { code?: string; errorMessage?: string } | undefined;
    const responseCode = responseData?.code;
    const errorMessage = responseData?.errorMessage;

    // 퇴사자 등 비활성 계정 → 재인증 시도 없이 안내 메시지 표시
    const ACCOUNT_DISABLED_MESSAGE = '현재 사용할 수 없는 계정입니다. 관리자에게 문의해 주세요.';
    if (errorMessage === ACCOUNT_DISABLED_MESSAGE) {
      tokenStorage.setToken(null);
      emitAuthFailure('ACCOUNT_DISABLED');
      return Promise.reject(mapAxiosError(error));
    }

    // INVALID_TOKEN, code 없는 401, 알 수 없는 code → refresh 생략, 즉시 재인증
    if (responseCode !== AUTH_ERROR_CODES.TOKEN_EXPIRED) {
      tokenStorage.setToken(null);
      emitAuthFailure('INVALID_TOKEN');
      return Promise.reject(mapAxiosError(error));
    }

    // TOKEN_EXPIRED — 이미 재시도한 요청이면 추가 refresh 없이 reject
    if (originalRequest._retry) {
      return Promise.reject(mapAxiosError(error));
    }

    // TOKEN_EXPIRED — silent refresh 1회 시도
    originalRequest._retry = true;
    try {
      const newToken = await refreshToken();
      if (newToken) {
        tokenStorage.setToken(newToken);
        originalRequest.headers = originalRequest.headers || {};
        (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return axiosClient(originalRequest);
      }
      // null 반환 = refresh 실패
      tokenStorage.setToken(null);
      emitAuthFailure('TOKEN_EXPIRED');
      return Promise.reject(mapAxiosError(error));
    } catch {
      tokenStorage.setToken(null);
      emitAuthFailure('TOKEN_EXPIRED');
      return Promise.reject(mapAxiosError(error));
    }
  },
);

export default axiosClient;
